import { useState } from 'react';
import type { FormEvent } from 'react';
import { api, ApiError } from '../lib/api';
import DateInput from '../components/DateInput';
import { isValidLatamDate, parseLatamDate } from '../lib/dates';

interface Values {
  date: string;
  reference: string;
  text: string;
  evangelio_lectura: string;
  reflection: string;
  image_url: string;
}

const EMPTY: Values = {
  date: '', reference: '', text: '', evangelio_lectura: '', reflection: '', image_url: '',
};

export default function GospelPage() {
  const [values, setValues] = useState<Values>(EMPTY);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState<{ kind: 'error' | 'success'; text: string } | null>(null);
  const [imageBroken, setImageBroken] = useState(false);

  const onChange = (k: keyof Values, v: string) => setValues(prev => ({ ...prev, [k]: v }));

  // Solo se intenta previsualizar cuando el valor ya parece una URL completa,
  // para no pedir imágenes a medio escribir en cada pulsación de tecla.
  const showPreview = /^https?:\/\/\S+$/i.test(values.image_url.trim());

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setFeedback(null);
    try {
      if (!isValidLatamDate(values.date)) {
        setSaving(false);
        setFeedback({ kind: 'error', text: 'La fecha debe tener formato dd/mm/aaaa' });
        return;
      }
      await api.post('/admin/gospel', {
        ...values,
        date: parseLatamDate(values.date),
        author: 'Delegación de Evangelización Digital',
      });
      setFeedback({ kind: 'success', text: 'Evangelio publicado en la app y en la web' });
      setValues(EMPTY);
      setImageBroken(false);
    } catch (err) {
      setFeedback({ kind: 'error', text: err instanceof ApiError ? err.message : 'No se pudo publicar' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <h1 className="page-title">Evangelio del día</h1>
      {feedback && (
        <div className={'alert ' + (feedback.kind === 'error' ? 'alert-error' : 'alert-success')}>
          {feedback.text}
        </div>
      )}
      <form className="card" onSubmit={onSubmit}>
        <div className="field">
          <label>Fecha (dd/mm/aaaa) *</label>
          <DateInput required value={values.date} onChange={(v) => onChange('date', v)} placeholder="dd/mm/aaaa" testId="date-gospel" />
        </div>
        <div className="field">
          <label>Referencia (ej: Mc 16, 15) *</label>
          <input required value={values.reference} onChange={e => onChange('reference', e.target.value)} />
        </div>
        <div className="field">
          <label>Texto del Evangelio</label>
          <textarea value={values.text} onChange={e => onChange('text', e.target.value)} rows={5} />
        </div>
        <div className="field">
          <label>Lectura completa del Evangelio</label>
          <textarea value={values.evangelio_lectura} onChange={e => onChange('evangelio_lectura', e.target.value)} rows={5} />
        </div>
        <div className="field">
          <label>Reflexión</label>
          <textarea value={values.reflection} onChange={e => onChange('reflection', e.target.value)} rows={6} />
        </div>
        <div className="field">
          <label>URL de imagen (opcional)</label>
          <input
            type="url"
            value={values.image_url}
            onChange={e => { onChange('image_url', e.target.value); setImageBroken(false); }}
            placeholder="https://res.cloudinary.com/.../evangelio.jpg"
          />
          <small>
            Debe ser el enlace <strong>directo a la imagen</strong> (termina en .jpg, .png o .webp),
            no un enlace de galería ni de «compartir». Si no ves la miniatura aquí, tampoco se verá en la app.
          </small>
          {showPreview && (
            imageBroken ? (
              <div className="alert alert-error" style={{ marginTop: 8 }}>
                No se puede cargar esa imagen. Revisa el enlace.
              </div>
            ) : (
              <img
                src={values.image_url}
                alt="Previsualización"
                onError={() => setImageBroken(true)}
                style={{ marginTop: 8, maxWidth: '100%', maxHeight: 220, borderRadius: 8, display: 'block' }}
              />
            )
          )}
        </div>
        <button type="submit" className="btn" disabled={saving}>
          {saving ? 'Publicando…' : 'Publicar Evangelio'}
        </button>
      </form>
    </>
  );
}
